import { alpha } from '@mui/material/styles';

import { globalStyles } from './globalStyles';
import { Palette } from './daylit';

const createSoftButtonStyles = color => ({
    'color': color,
    'backgroundColor': alpha(color, 0.16),
    'border': '1px solid transparent',
    ':hover': {
        backgroundColor: alpha(color, 0.24),
        boxShadow: 'none',
    },
    ':active': {
        backgroundColor: alpha(color, 0.32),
    },
    ':focus-visible': {
        outline: `2px solid ${alpha(color, 0.48)}`,
        outlineOffset: 2,
    },
    '&.Mui-disabled': {
        color: alpha(color, 0.4),
        backgroundColor: alpha(color, 0.12),
        borderColor: 'transparent',
    },
});

// const lendicaPalette = {
//     primary: { light: '#8283BE', dark: '#282761', main: '#3C41E7', contrast: '#FCFCFD' },
//     secondary: { main: '#0E0E2C', light: '#6E6E80', dark: '#3E3E56' },
//     success: { main: '#58A10E' },
//     error: { main: '#ED7A56', light: '#F4AF9A', dark: '#DD4718' },
//     warning: { main: '#FAAF6D', light: '#FCCEA6', dark: '#F98F34' },
//     action: { disabled: '#BABAC7', active: '#8C8CA1' },
//     info: {
//         main: '#4A4A68',
//         dark: '#3B3B53',
//         light: '#9292A4',
//     },
//     text: { primary: '#202024', secondary: '#4A4A68', disabled: '#8C8CA1' },
//     background: { paper: '#FCFCFD', default: '#F8F8FB', card: '#F2F2F8', cardLight: '#F8F8FB', cardBorder: '#EDEDF4' },
//     common: { black: '#202024' },
// }

const palette = {
    primary: { main: Palette.Burgundy, contrastText: Palette.PureWhite },
    secondary: { main: Palette.Navy },
    success: { main: '#58A10E' },
    error: { main: '#E5484D' },
    warning: {
        main: '#E5A100', // Golden amber
        contrastText: '#2F080F',
    },
    neutral: {
        main: '#404040', // Tailwind's nuetral gray 700'
    },
    action: {
        active: '#4D1520',
        hover: 'rgba(77, 21, 32, 0.08)',
    },
    info: {
        main: '#565362', // '70% of Navy'
        // main: '#3A73B4', // a muted blue
        contrastText: Palette.PureWhite,
    },
    text: {
        primary: Palette.DeepBurgundy, // '#202024'
        secondary: '#807678', // 70% of DeepBurgundy mixed with Canvas:#6C5154;
        disabled: '#C1B5B7', // 30% of DeepBurgundy mixed with PureWhite;
    },
    background: {
        paper: '#FCFCFD',
        default: '#FBF9F6',
        card: Palette.Canvas,
        cardLight: '#F8F8FB',
        cardBorder: '#EDEDF4',
    },
    common: {
        black: '#202024',
    },
    divider: '#E0D6D1',
    chart: {
        main: Palette.Sunrise,
    },
};

const softButtonVariants = [
    { color: 'primary', value: palette.primary.main },
    { color: 'secondary', value: palette.secondary.main },
    { color: 'success', value: palette.success.main },
    { color: 'error', value: palette.error.main },
    { color: 'warning', value: palette.warning.main },
    { color: 'info', value: palette.info.main },
    { color: 'neutral', value: palette.neutral.main },
].map(({ color, value }) => ({
    props: { variant: 'soft', color },
    style: createSoftButtonStyles(value),
}));

export const theme = {
    palette,
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            'Roboto',
            '"Segoe UI"',
            '"Helvetica Neue"',
            'sans-serif',
        ].join(','),
        fontWeightRegular: 400,
        fontWeightSemibold: 600,
        button: {
            fontWeight: 400,
            textTransform: 'none',
        },
        subtitle1: {
            fontWeight: 600,
        },
        subtitle2: {
            fontWeight: 500,
        },
        h4: {
            fontWeight: 700,
        },
        h5: {
            fontWeight: 200,
        },
        caption: {
            color: '#807678', // '#4A4A68'
            fontWeight: 400,
        },
        overline: {
            color: '#807678', // '#8C8CA1',
            fontSize: '0.6875rem',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: globalStyles,
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'text' },
                    style: {
                        'borderRadius': 6,
                        'boxShadow': 'none',
                        ':hover': {
                            boxShadow: 'none',
                        },
                    },
                },
                {
                    props: { variant: 'contained' },
                    style: {
                        // 'borderRadius': 100,
                        'borderRadius': 6,
                        'boxShadow': 'none',
                        ':hover': {
                            boxShadow: 'none',
                        },
                    },
                },
                {
                    props: { variant: 'outlined' },
                    style: {
                        'borderRadius': 6,
                        'boxShadow': 'none',
                        ':hover': {
                            boxShadow: 'none',
                        },
                    },
                },
                ...softButtonVariants,
            ],
        },
        MuiBottomNavigationAction: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        color: Palette.DeepBurgundy, // '#0E0E2C'
                    },
                },
            },
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
};
